// 浮层：手机是底部 sheet（全高），iPad / 桌面是右侧抽屉 400（文档 23 §6）。
// 规格照订单 FilterPanel；这里抽成通用外壳 + 单选 chips 筛选面板。建议主会话提取到 src/components。
import { useEffect, useState, type ReactNode } from 'react';
import { useFormFactor } from '@/lib/formFactor';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import s from './Sheet.module.css';

export function Sheet({ title, onClose, left, right, footer, layout = 'drawer', className, headerClassName, bodyClassName, footerClassName, children }: { title: string; onClose: () => void; left?: ReactNode; right?: ReactNode; footer?: ReactNode; layout?: 'drawer' | 'form'; className?: string; headerClassName?: string; bodyClassName?: string; footerClassName?: string; children: ReactNode }) {
  const sheet = useFormFactor() === 'phone';
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className={s.scrim} onClick={onClose}>
      <aside className={[sheet ? s.sheet : layout === 'form' ? s.form : s.drawer, className].filter(Boolean).join(' ')} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        {sheet && <div className={s.grab} />}
        <header className={[s.hd, headerClassName].filter(Boolean).join(' ')}>
          {left ?? <button type="button" className={s.x} onClick={onClose} aria-label="关闭"><Icon name="x" size={20} /></button>}
          <b>{title}</b>
          <span className={s.right}>{right}</span>
        </header>
        <div className={[s.bd, bodyClassName].filter(Boolean).join(' ')}>{children}</div>
        {footer && <footer className={[s.ft, footerClassName].filter(Boolean).join(' ')}>{footer}</footer>}
      </aside>
    </div>
  );
}

export type FilterGroup = {
  key: string;
  title: string;
  opts: { value: string; label: string; count?: number }[];
  /** 组标题右侧的入口（例如「分类」组 → 分类列表） */
  extra?: ReactNode;
};

/** 单选 chips 筛选（老接口每个参数只收一个值）；点「应用」才生效 */
export function FilterSheet({ groups, value, onApply, onClose, hint }: {
  groups: FilterGroup[]; value: Record<string, string | undefined>; onApply: (v: Record<string, string | undefined>) => void; onClose: () => void; hint?: ReactNode;
}) {
  const [f, setF] = useState(value);
  const picked = groups.filter((g) => f[g.key]).length;
  return (
    <Sheet
      title="筛选"
      onClose={onClose}
      right={<button type="button" className={s.reset} onClick={() => setF({})}>重置</button>}
      footer={<Button variant="primary" size="lg" block onClick={() => onApply(Object.fromEntries(groups.map((g) => [g.key, f[g.key]])))}>应用{picked ? `（${picked} 项）` : ''}</Button>}
    >
      {groups.map((g) => (
        <section key={g.key} className={s.sec}>
          <h4>{g.title}{f[g.key] && <span>已选 1</span>}{g.extra && <em>{g.extra}</em>}</h4>
          {g.opts.length ? (
            <div className={s.chips}>
              {g.opts.map((o) => (
                <button key={o.value} type="button" className={`${s.chip} ${f[g.key] === o.value ? s.on : ''}`} aria-pressed={f[g.key] === o.value}
                  onClick={() => setF({ ...f, [g.key]: f[g.key] === o.value ? undefined : o.value })}>
                  {o.label}{o.count != null && <i>{o.count}</i>}
                </button>
              ))}
            </div>
          ) : <p className={s.none}>暂无可选项</p>}
        </section>
      ))}
      {hint && <p className={s.hint}>{hint}</p>}
    </Sheet>
  );
}
